/* eslint-disable class-methods-use-this */
import { ICalendarDateRange } from '@models';
import { EntityDto } from 'services/dto';
import { PagedResultDto } from '../dto/pagedResultDto';
import http from '../httpService';
import {
  CalendarOrder,
  CreateOrder,
  CreateOrderWorkLog,
  CreateOrUpdateOrderNote,
  CreateServiceOrder,
  DocumentDto,
  Order,
  OrderApprovalHistory,
  OrderChange,
  OrderNote,
  OrderPresence,
  OrderPresenceRequest,
  OrderWorkLog,
  PagedDocumentRequest,
  PagedOrderApprovalHistoryRequest,
  PagedOrderNotes,
  PagedOrderPresenceRequest,
  PagedOrderWorkLogRequest,
  PagedServiceOrderRequest,
  PagedUnassignedOrderRequest,
  PagedVehicleHistoryOrdersRequest,
  ReviseOrderApprovalRequest,
  ReviseOrderApprovalResult,
  ServiceOrder,
  UnassignedOrder,
  UnassignedOrderCounts,
  UnassignedOrderCountsRequest,
  UpdateOrder,
  UpdateOrderApprovalHistoryRequest,
  UpdateOrderWorkLog,
  UpdateServiceOrder,
  VehicleHistoryOrder,
} from './dto/serviceOrder';
import { OrderSummary } from './dto/orderSummary';
import { CreateServiceOrderDocument, ServiceOrderExtra } from './dto/serviceOrderExtra';

class OrderService {
  public async getCalendarAll(request: ICalendarDateRange): Promise<CalendarOrder[]> {
    const result = await http.get('api/services/app/Order/GetCalendarAll', {
      params: request,
    });
    return result.data.result;
  }

  /** Money totals over the whole filtered set; the same filters as getAll, without paging. */
  public async getSummary(
    request: Omit<PagedServiceOrderRequest, 'maxResultCount' | 'skipCount'>
  ): Promise<OrderSummary> {
    const result = await http.get('api/services/app/Order/GetSummary', { params: request });
    return result.data.result;
  }

  public async getAllUnassigned(
    request: PagedUnassignedOrderRequest
  ): Promise<PagedResultDto<UnassignedOrder>> {
    const result = await http.get('api/services/app/Order/GetAllUnassigned', {
      params: request,
    });
    return result.data.result;
  }

  public async getUnassignedCounts(
    request?: UnassignedOrderCountsRequest
  ): Promise<UnassignedOrderCounts> {
    const result = await http.get('api/services/app/Order/GetUnassignedCounts', {
      params: request,
    });
    return result.data.result;
  }

  public async get(entityDto: EntityDto<string>): Promise<Order> {
    const result = await http.get('api/services/app/Order/Get', { params: entityDto });
    return result.data.result;
  }

  public async getRelatedOrders(entityDto: EntityDto<string>): Promise<Order[]> {
    const result = await http.get('api/services/app/Order/GetRelatedOrders', { params: entityDto });
    return result.data.result;
  }

  public async getAll(request: PagedServiceOrderRequest): Promise<PagedResultDto<Order>> {
    const result = await http.get('api/services/app/Order/GetAll', {
      params: request,
    });
    return result.data.result;
  }

  public async getOrderNotes(request: PagedOrderNotes): Promise<PagedResultDto<OrderNote>> {
    const result = await http.get('api/services/app/OrderNote/GetAll', {
      params: request,
    });
    return result.data.result;
  }

  public async createOrderNote(request: CreateOrUpdateOrderNote): Promise<OrderNote> {
    const result = await http.post('api/services/app/OrderNote/Create', request);
    return result.data.result;
  }

  public async updateOrderNote(request: CreateOrUpdateOrderNote): Promise<OrderNote> {
    const result = await http.put('api/services/app/OrderNote/Update', request);
    return result.data.result;
  }

  public async getWorkLogs(request: PagedOrderWorkLogRequest): Promise<PagedResultDto<OrderWorkLog>> {
    const result = await http.get('api/services/app/OrderWorkLog/GetAll', {
      params: request,
    });
    return result.data.result;
  }

  public async createWorkLog(request: CreateOrderWorkLog): Promise<OrderWorkLog> {
    const result = await http.post('api/services/app/OrderWorkLog/Create', request);
    return result.data.result;
  }

  public async updateWorkLog(request: UpdateOrderWorkLog): Promise<OrderWorkLog> {
    const result = await http.put('api/services/app/OrderWorkLog/Update', request);
    return result.data.result;
  }

  public async deleteWorkLog(entityDto: EntityDto<string>): Promise<void> {
    await http.delete('api/services/app/OrderWorkLog/Delete', { params: entityDto });
  }

  public async getPresences(request: PagedOrderPresenceRequest): Promise<PagedResultDto<OrderPresence>> {
    const result = await http.get('api/services/app/OrderPresence/GetAll', {
      params: request,
    });
    return result.data.result;
  }

  public async createPresence(request: OrderPresenceRequest): Promise<OrderPresence> {
    const result = await http.post('api/services/app/OrderPresence/Create', request);
    return result.data.result;
  }

  public async deletePresence(entityDto: EntityDto<string>): Promise<void> {
    const result = await http.delete('api/services/app/OrderPresence/Delete', {
      params: entityDto,
    });
    return result.data.result;
  }

  public async getVehicleHistoryOrders(
    request: PagedVehicleHistoryOrdersRequest
  ): Promise<PagedResultDto<VehicleHistoryOrder>> {
    const result = await http.get('api/services/app/Order/GetVehicleHistoryOrders', {
      params: request,
    });
    return result.data.result;
  }

  public async getGenerateInvoiceOrders(ids: string[]): Promise<Order[]> {
    const result = await http.post('api/services/app/Order/GetOrdersById', ids);
    return result.data.result;
  }

  public async getAllChanges(
    pagedFilterAndSortedRequest: PagedServiceOrderRequest
  ): Promise<PagedResultDto<OrderChange>> {
    const result = await http.get('api/services/app/OrderChange/GetAll', {
      params: pagedFilterAndSortedRequest,
    });
    return result.data.result;
  }

  public async create(request: CreateOrder): Promise<Order> {
    const result = await http.post('api/services/app/Order/Create', request);
    return result.data.result;
  }

  public async update(request: UpdateOrder): Promise<Order> {
    const result = await http.put('api/services/app/Order/Update', request);
    return result.data.result;
  }

  public async updateCalendar(request: UpdateOrder): Promise<Order> {
    const result = await http.put('api/services/app/Order/UpdateCalendar', request);
    return result.data.result;
  }

  public async unassign(entityDto: EntityDto<string>): Promise<UnassignedOrder> {
    const result = await http.post('api/services/app/Order/Unassign', entityDto);
    return result.data.result;
  }

  public async delete(entityDto: EntityDto<string>): Promise<ServiceOrder> {
    const result = await http.delete('api/services/app/Order/Delete', { params: entityDto });
    return result.data.result;
  }

  public async createServiceOrder(serviceOrder: CreateServiceOrder): Promise<ServiceOrder> {
    const result = await http.post('api/services/app/ServiceOrder/Create', serviceOrder);
    return result.data.result;
  }

  public async updateServiceOrder(request: UpdateServiceOrder): Promise<ServiceOrder> {
    const result = await http.put('api/services/app/ServiceOrder/Update', request);
    return result.data.result;
  }

  public async deleteServiceOrder(entityDto: EntityDto<string>) {
    await http.delete('api/services/app/ServiceOrder/Delete', {
      params: entityDto,
    });
  }

  public async createExtra(serviceOrderExtra: ServiceOrderExtra): Promise<ServiceOrderExtra> {
    const result = await http.post('api/services/app/ServiceOrderExtra/Create', serviceOrderExtra);
    return result.data.result;
  }

  public async updateExtra(serviceOrderExtra: ServiceOrderExtra) {
    const result = await http.put('api/services/app/ServiceOrderExtra/Update', serviceOrderExtra);
    return result.data.result;
  }

  public async deleteExtra(entityDto: EntityDto<string>): Promise<ServiceOrderExtra> {
    const result = await http.delete('api/services/app/ServiceOrderExtra/Delete', {
      params: entityDto,
    });
    return result.data.result;
  }

  public async returnExtraParts(
    items: { id: string; destination: string; quantity?: number; documentId?: string }[]
  ): Promise<ServiceOrderExtra[]> {
    const result = await http.post('api/services/app/ServiceOrderExtra/ReturnParts', { items });
    return result.data.result;
  }

  public async getAllDocuments(
    pagedFilterAndSortedRequest: PagedDocumentRequest
  ): Promise<PagedResultDto<DocumentDto>> {
    const result = await http.get('api/services/app/OrderDocument/GetDocuments', {
      params: pagedFilterAndSortedRequest,
    });
    return result.data.result;
  }

  public async createDocument(input: CreateServiceOrderDocument) {
    const result = await http.post('api/services/app/OrderDocument/CreateDocument', input);
    return result.data.result;
  }

  public async deleteDocument(entityDto: EntityDto<string>) {
    const result = await http.delete('api/services/app/OrderDocument/Delete', {
      params: entityDto,
    });
    return result.data.result;
  }

  public async getApprovalHistory(
    request: PagedOrderApprovalHistoryRequest
  ): Promise<PagedResultDto<OrderApprovalHistory>> {
    const result = await http.get('api/services/app/OrderApprovalHistory/GetAll', {
      params: request,
    });
    return result.data.result;
  }

  public async updateApprovalHistory(
    request: UpdateOrderApprovalHistoryRequest
  ): Promise<OrderApprovalHistory> {
    const result = await http.put('api/services/app/OrderApprovalHistory/Update', request);
    return result.data.result;
  }

  public async reviseApproval(
    request: ReviseOrderApprovalRequest
  ): Promise<ReviseOrderApprovalResult> {
    const result = await http.post('api/services/app/OrderApprovalHistory/Revise', request);
    return result.data.result;
  }

  public async sendEstimateEmail(orderId: string, email?: string): Promise<void> {
    await http.post('api/services/app/Order/SendEstimateEmail', { id: orderId, email: email || undefined });
  }
}

export default new OrderService();
